// The one list of the exits of the customer area (NMC-2546).
// A component asks this list. It never decides by itself.
// To change one exit, change one entry of OUTGOING_LINKS.
// Today the logo, the no-complete-order redirect, the login page chrome
// (src/shared/Layout.tsx), the login join-us link and the referral card read
// this list.
// The other entries are descriptive: their code still holds its own target.

// The only place that holds the date of the switch.
// The mechanism may change (for example a Strapi value, to avoid a deploy).
// The repo has no client flag today. Only change `isMobileClubSwitchDone`.
export const MOBILE_CLUB_SWITCH_DATE = new Date("2026-11-16T00:00:00+01:00");

export function isMobileClubSwitchDone(now: Date = new Date()): boolean {
  return now.getTime() >= MOBILE_CLUB_SWITCH_DATE.getTime();
}

type AfterSwitch =
  | { action: "unchanged" }
  | { action: "removed" }
  | { action: "redirected"; target: string };

export type OutgoingLinkId =
  | "logo"
  | "noCompleteOrderRedirect"
  | "incompleteOrderRedirect"
  | "logout"
  | "loginPageChrome"
  | "loginJoinUsLink"
  | "referral"
  | "swapBanner"
  | "insurancePdf";

type OutgoingLink = {
  // Where the exit points before the switch.
  target: string;
  // What happens to the exit from the switch date.
  afterSwitch: AfterSwitch;
  reason: string;
};

// An exit that the code reads with no "removed" case: it always has a target.
type RedirectedLink = OutgoingLink & {
  afterSwitch: { action: "redirected"; target: string };
};

export const OUTGOING_LINKS: Record<OutgoingLinkId, OutgoingLink> & {
  noCompleteOrderRedirect: RedirectedLink;
} = {
  logo: {
    target: "/",
    afterSwitch: { action: "removed" },
    reason:
      "The logo stays, but it is not a link. The mobile menu is the same.",
  },
  noCompleteOrderRedirect: {
    target: "/",
    afterSwitch: { action: "redirected", target: "/login?closed=1" },
    reason:
      "A customer with no complete order goes to the login page. ?closed=1 shows the closed message.",
  },
  incompleteOrderRedirect: {
    target: "/commander/recapitulatif",
    afterSwitch: { action: "unchanged" },
    reason: "A customer with an open order goes to the order recap (NMC-2545).",
  },
  logout: {
    target: "/connexion",
    afterSwitch: { action: "unchanged" },
    reason: "Logout goes to /connexion. next.config.js sends it to /login.",
  },
  loginPageChrome: {
    target: "/login",
    afterSwitch: { action: "removed" },
    reason:
      "The login page loses its header menu, banner and footer. It is static: the build reads this entry, and the browser reads it again after mount.",
  },
  loginJoinUsLink: {
    target: "/smartphone",
    afterSwitch: { action: "removed" },
    reason:
      "Decision of Robin, 2026-10-16: the login form loses its join-us link. The page is static: the browser reads this entry after mount.",
  },
  referral: {
    target: "https://www.mobile.club/?code-parrain=",
    afterSwitch: { action: "removed" },
    reason:
      "Decision of Robin, 2026-10-21: remove the referral card during the transition. Only non-migrated customers see it, and the mobile.club link redirects to Next from the switch date.",
  },
  swapBanner: {
    target: "/swap/choix-smartphone",
    afterSwitch: { action: "unchanged" },
    reason: "NMC-2545 owns the swap. This ticket does not change it.",
  },
  insurancePdf: {
    target: "/assets/insurance-cgl.pdf",
    afterSwitch: { action: "unchanged" },
    reason: "It is a file, not a page.",
  },
};

// The target to use now, or null when the exit is removed.
export function getOutgoingLinkTarget(
  id: OutgoingLinkId,
  now?: Date,
): string | null {
  const link = OUTGOING_LINKS[id];

  if (!isMobileClubSwitchDone(now)) {
    return link.target;
  }

  switch (link.afterSwitch.action) {
    case "removed":
      return null;
    case "redirected":
      return link.afterSwitch.target;
    case "unchanged":
      return link.target;
  }
}

// The target of the no-complete-order redirect. It is never null.
export function getNoCompleteOrderRedirectTarget(now?: Date): string {
  const link = OUTGOING_LINKS.noCompleteOrderRedirect;

  return isMobileClubSwitchDone(now) ? link.afterSwitch.target : link.target;
}
